import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Sparkles, Users, Clock, Calendar as CalendarIcon, Plus, Settings, DollarSign, Scissors, User, Building2, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import api from '@/lib/api';

export default function SpaPage() {
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Dialog states
  const [showAddCenter, setShowAddCenter] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddTherapist, setShowAddTherapist] = useState(false);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Form states
  const [newCenter, setNewCenter] = useState({ name: '', description: '', location: '' });
  const [newService, setNewService] = useState({ name: '', description: '', duration_minutes: 60, price: 0, category_id: '' });
  const [newTherapist, setNewTherapist] = useState({ first_name: '', last_name: '', email: '', phone: '', skills: [] });
  const [newRoom, setNewRoom] = useState({ name: '', room_number: '', room_type: 'single', capacity: 1 });
  const [newAppointment, setNewAppointment] = useState({
    service_id: '',
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    therapist_id: '',
    room_id: '',
    appointment_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: ''
  });

  // Get property_id from auth context
  const getSelectedPropertyId = () => {
    const storedProperty = localStorage.getItem('hms_current_property');
    if (storedProperty) {
      try {
        const prop = JSON.parse(storedProperty);
        return prop.id || '';
      } catch {
        return '';
      }
    }
    return '';
  };
  
  const selectedProperty = getSelectedPropertyId();

  useEffect(() => {
    if (selectedProperty) {
      loadCenters();
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedCenter) {
      loadCenterData();
    }
  }, [selectedCenter, selectedDate]);

  const loadCenters = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/spa/centers?property_id=${selectedProperty}`);
      setCenters(res.data || []);
      if (res.data?.length > 0 && !selectedCenter) {
        setSelectedCenter(res.data[0]);
      }
    } catch (error) {
      console.error('Error loading spa centers:', error);
      toast.error('Failed to load spa centers');
    } finally {
      setLoading(false);
    }
  };

  const loadCenterData = async () => {
    if (!selectedCenter) return;
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [servicesRes, categoriesRes, therapistsRes, roomsRes, appointmentsRes, statsRes] = await Promise.all([
        api.get(`/spa/centers/${selectedCenter.id}/services`),
        api.get(`/spa/centers/${selectedCenter.id}/categories`),
        api.get(`/spa/centers/${selectedCenter.id}/therapists`),
        api.get(`/spa/centers/${selectedCenter.id}/rooms`),
        api.get(`/spa/appointments?center_id=${selectedCenter.id}&date_from=${dateStr}&date_to=${dateStr}`),
        api.get(`/spa/stats?center_id=${selectedCenter.id}`)
      ]);
      
      setServices(servicesRes.data || []);
      setCategories(categoriesRes.data || []);
      setTherapists(therapistsRes.data || []);
      setRooms(roomsRes.data || []);
      setAppointments(appointmentsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading spa data:', error);
    }
  };

  const handleCreateCenter = async () => {
    try {
      await api.post(`/spa/centers?property_id=${selectedProperty}`, newCenter);
      toast.success('Spa center created');
      setShowAddCenter(false);
      setNewCenter({ name: '', description: '', location: '' });
      loadCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create spa center');
    }
  };

  const handleCreateService = async () => {
    if (!selectedCenter) return;
    try {
      // Clean up uncategorized value before sending
      const serviceData = {
        ...newService,
        category_id: newService.category_id === 'uncategorized' ? null : newService.category_id || null
      };
      await api.post(`/spa/centers/${selectedCenter.id}/services`, serviceData);
      toast.success('Service created');
      setShowAddService(false);
      setNewService({ name: '', description: '', duration_minutes: 60, price: 0, category_id: '' });
      loadCenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    }
  };

  const handleCreateTherapist = async () => {
    if (!selectedCenter) return;
    try {
      await api.post(`/spa/centers/${selectedCenter.id}/therapists`, newTherapist);
      toast.success('Therapist added');
      setShowAddTherapist(false);
      setNewTherapist({ first_name: '', last_name: '', email: '', phone: '', skills: [] });
      loadCenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add therapist');
    }
  };

  const handleCreateRoom = async () => {
    if (!selectedCenter) return;
    try {
      await api.post(`/spa/centers/${selectedCenter.id}/rooms`, newRoom);
      toast.success('Room added');
      setShowAddRoom(false);
      setNewRoom({ name: '', room_number: '', room_type: 'single', capacity: 1 });
      loadCenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add room');
    }
  };

  const handleCheckAvailability = async () => {
    if (!selectedCenter || !newAppointment.service_id || !newAppointment.appointment_date) return;
    try {
      const therapistId = newAppointment.therapist_id && newAppointment.therapist_id !== 'any' ? newAppointment.therapist_id : null;
      const res = await api.get(`/spa/centers/${selectedCenter.id}/availability?service_id=${newAppointment.service_id}&appointment_date=${newAppointment.appointment_date}${therapistId ? `&therapist_id=${therapistId}` : ''}`);
      setAvailableSlots(res.data?.available_slots || []);
    } catch (error) {
      toast.error('Failed to check availability');
    }
  };

  const handleCreateAppointment = async () => {
    if (!selectedCenter) return;
    try {
      const appointmentData = {
        ...newAppointment,
        therapist_id: newAppointment.therapist_id === 'any' ? null : newAppointment.therapist_id || null,
        room_id: newAppointment.room_id === 'auto' ? null : newAppointment.room_id || null,
        guest_phone: newAppointment.guest_phone || null,
        guest_email: newAppointment.guest_email || null,
      };
      await api.post(`/spa/centers/${selectedCenter.id}/appointments`, appointmentData);
      toast.success('Appointment booked');
      setShowAddAppointment(false);
      setNewAppointment({
        service_id: '',
        guest_name: '',
        guest_phone: '',
        guest_email: '',
        therapist_id: '',
        room_id: '',
        appointment_date: format(new Date(), 'yyyy-MM-dd'),
        start_time: ''
      });
      setAvailableSlots([]);
      loadCenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    }
  };

  const handleUpdateAppointmentStatus = async (appointmentId, status) => {
    try {
      await api.put(`/spa/appointments/${appointmentId}?status=${status}`);
      toast.success(`Appointment ${status}`);
      loadCenterData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    const reason = prompt('Cancellation reason:');
    if (!reason) return;
    try {
      await api.post(`/spa/appointments/${appointmentId}/cancel?reason=${encodeURIComponent(reason)}`);
      toast.success('Appointment cancelled');
      loadCenterData();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      booked: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-indigo-100 text-indigo-800',
      checked_in: 'bg-amber-100 text-amber-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={variants[status] || 'bg-slate-100 text-slate-800'}>{status.replace('_', ' ')}</Badge>;
  };

  if (!selectedProperty) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-10 text-center">
            <Building2 className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold">No Property Selected</h3>
            <p className="text-slate-500">Please select a property from the dashboard to manage spa services.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="spa-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Spa & Wellness</h1>
          <p className="text-slate-500">Manage spa services, therapists, and appointments</p>
        </div>
        <div className="flex gap-2">
          {selectedCenter && (
            <Select value={selectedCenter?.id} onValueChange={(v) => setSelectedCenter(centers.find(c => c.id === v))}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {centers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={showAddCenter} onOpenChange={setShowAddCenter}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-spa-center-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Spa Center
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Spa Center</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Name</Label>
                  <Input value={newCenter.name} onChange={(e) => setNewCenter({...newCenter, name: e.target.value})} placeholder="e.g., Serenity Spa" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={newCenter.description} onChange={(e) => setNewCenter({...newCenter, description: e.target.value})} />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={newCenter.location} onChange={(e) => setNewCenter({...newCenter, location: e.target.value})} placeholder="e.g., Ground Floor" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddCenter(false)}>Cancel</Button>
                <Button onClick={handleCreateCenter}>Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Appointments</p>
                  <p className="text-2xl font-bold">{stats.today?.total_appointments || 0}</p>
                </div>
                <CalendarIcon className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed Today</p>
                  <p className="text-2xl font-bold">{stats.today?.completed || 0}</p>
                </div>
                <Check className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Revenue</p>
                  <p className="text-2xl font-bold">{stats.today?.revenue?.toLocaleString() || 0} ETB</p>
                </div>
                <DollarSign className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Therapists</p>
                  <p className="text-2xl font-bold">{stats.active_therapists || 0}</p>
                </div>
                <Users className="h-8 w-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Center */}
      {centers.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Sparkles className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold">No Spa Centers</h3>
            <p className="text-slate-500 mb-4">Create your first spa center to get started</p>
            <Button onClick={() => setShowAddCenter(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Spa Center
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="appointments" className="space-y-4">
          <TabsList>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="therapists">Therapists</TabsTrigger>
            <TabsTrigger value="rooms">Treatment Rooms</TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Appointments</CardTitle>
                  <CardDescription>View and manage spa appointments</CardDescription>
                </div>
                <div className="flex gap-2 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {format(selectedDate, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} initialFocus />
                    </PopoverContent>
                  </Popover>
                  <Dialog open={showAddAppointment} onOpenChange={setShowAddAppointment}>
                    <DialogTrigger asChild>
                      <Button data-testid="book-appointment-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Book Appointment
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Book Appointment</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                        <div>
                          <Label>Service</Label>
                          <Select value={newAppointment.service_id} onValueChange={(v) => {
                            setNewAppointment({...newAppointment, service_id: v});
                            setAvailableSlots([]);
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {services.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name} - {s.duration_minutes}min - {s.price} ETB</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Date</Label>
                            <Input type="date" value={newAppointment.appointment_date} onChange={(e) => {
                              setNewAppointment({...newAppointment, appointment_date: e.target.value});
                              setAvailableSlots([]);
                            }} />
                          </div>
                          <div>
                            <Label>Therapist (Optional)</Label>
                            <Select value={newAppointment.therapist_id || ''} onValueChange={(v) => {
                              setNewAppointment({...newAppointment, therapist_id: v || ''});
                              setAvailableSlots([]);
                            }}>
                              <SelectTrigger>
                                <SelectValue placeholder="Any therapist" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="any">Any available</SelectItem>
                                {therapists.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <Button variant="outline" size="sm" onClick={handleCheckAvailability} disabled={!newAppointment.service_id || !newAppointment.appointment_date}>
                          Check Availability
                        </Button>
                        
                        {availableSlots.length > 0 && (
                          <div>
                            <Label>Available Times</Label>
                            <div className="grid grid-cols-4 gap-2 mt-2">
                              {availableSlots.map(slot => (
                                <Button key={slot.start_time} variant={newAppointment.start_time === slot.start_time ? 'default' : 'outline'} size="sm" onClick={() => setNewAppointment({...newAppointment, start_time: slot.start_time})}>
                                  {slot.start_time}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="border-t pt-4">
                          <Label className="text-sm font-medium">Guest Details</Label>
                        </div>
                        <div>
                          <Label>Guest Name</Label>
                          <Input value={newAppointment.guest_name} onChange={(e) => setNewAppointment({...newAppointment, guest_name: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Phone</Label>
                            <Input value={newAppointment.guest_phone} onChange={(e) => setNewAppointment({...newAppointment, guest_phone: e.target.value})} />
                          </div>
                          <div>
                            <Label>Email</Label>
                            <Input type="email" value={newAppointment.guest_email} onChange={(e) => setNewAppointment({...newAppointment, guest_email: e.target.value})} />
                          </div>
                        </div>
                        <div>
                          <Label>Room (Optional)</Label>
                          <Select value={newAppointment.room_id || ''} onValueChange={(v) => setNewAppointment({...newAppointment, room_id: v || ''})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Auto-assign" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto-assign</SelectItem>
                              {rooms.map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.name} ({r.room_type})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddAppointment(false)}>Cancel</Button>
                        <Button onClick={handleCreateAppointment} disabled={!newAppointment.service_id || !newAppointment.start_time || !newAppointment.guest_name}>Book Appointment</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <CalendarIcon className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No appointments for {format(selectedDate, 'MMMM d, yyyy')}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Guest</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Therapist</TableHead>
                        <TableHead>Room</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map(apt => (
                        <TableRow key={apt.id}>
                          <TableCell className="font-medium">{apt.start_time} - {apt.end_time}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{apt.guest_name}</p>
                              <p className="text-sm text-slate-500">{apt.guest_phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>{apt.service_name || apt.spa_services?.name}</TableCell>
                          <TableCell>{apt.therapist_name || (apt.spa_therapists ? `${apt.spa_therapists.first_name} ${apt.spa_therapists.last_name}` : '-')}</TableCell>
                          <TableCell>{apt.room_name || apt.spa_rooms?.name || '-'}</TableCell>
                          <TableCell>{apt.price?.toLocaleString()} ETB</TableCell>
                          <TableCell>{getStatusBadge(apt.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {apt.status === 'booked' && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => handleUpdateAppointmentStatus(apt.id, 'confirmed')}>
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleCancelAppointment(apt.id)}>
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </>
                              )}
                              {apt.status === 'confirmed' && (
                                <Button size="sm" onClick={() => handleUpdateAppointmentStatus(apt.id, 'checked_in')}>
                                  Check In
                                </Button>
                              )}
                              {apt.status === 'checked_in' && (
                                <Button size="sm" onClick={() => handleUpdateAppointmentStatus(apt.id, 'in_progress')}>
                                  Start
                                </Button>
                              )}
                              {apt.status === 'in_progress' && (
                                <Button size="sm" onClick={() => handleUpdateAppointmentStatus(apt.id, 'completed')}>
                                  Complete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Spa Services</CardTitle>
                  <CardDescription>Manage treatments and services offered</CardDescription>
                </div>
                <Dialog open={showAddService} onOpenChange={setShowAddService}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-service-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Service</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Service Name</Label>
                        <Input value={newService.name} onChange={(e) => setNewService({...newService, name: e.target.value})} placeholder="e.g., Swedish Massage" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={newService.description} onChange={(e) => setNewService({...newService, description: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Duration (minutes)</Label>
                          <Input type="number" value={newService.duration_minutes} onChange={(e) => setNewService({...newService, duration_minutes: parseInt(e.target.value) || 60})} />
                        </div>
                        <div>
                          <Label>Price (ETB)</Label>
                          <Input type="number" value={newService.price} onChange={(e) => setNewService({...newService, price: parseFloat(e.target.value) || 0})} />
                        </div>
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={newService.category_id || ''} onValueChange={(v) => setNewService({...newService, category_id: v || ''})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="uncategorized">Uncategorized</SelectItem>
                            {categories.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddService(false)}>Cancel</Button>
                      <Button onClick={handleCreateService}>Add Service</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {services.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <Scissors className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No services configured</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map(service => (
                      <Card key={service.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              {service.spa_service_categories?.name && (
                                <Badge variant="outline" className="mb-2">{service.spa_service_categories.name}</Badge>
                              )}
                              <h4 className="font-semibold">{service.name}</h4>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{service.description || 'No description'}</p>
                            </div>
                            <Sparkles className="h-5 w-5 text-purple-500" />
                          </div>
                          <div className="mt-4 flex items-center justify-between text-sm">
                            <span className="flex items-center text-slate-500">
                              <Clock className="h-4 w-4 mr-1" />
                              {service.duration_minutes} min
                            </span>
                            <span className="font-bold text-emerald-600">{service.price?.toLocaleString()} ETB</span>
                          </div>
                          {!service.is_active && <Badge variant="secondary" className="mt-2">Inactive</Badge>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Therapists Tab */}
          <TabsContent value="therapists">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Therapists</CardTitle>
                  <CardDescription>Manage spa therapists and their schedules</CardDescription>
                </div>
                <Dialog open={showAddTherapist} onOpenChange={setShowAddTherapist}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-therapist-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Therapist
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Therapist</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>First Name</Label>
                          <Input value={newTherapist.first_name} onChange={(e) => setNewTherapist({...newTherapist, first_name: e.target.value})} />
                        </div>
                        <div>
                          <Label>Last Name</Label>
                          <Input value={newTherapist.last_name} onChange={(e) => setNewTherapist({...newTherapist, last_name: e.target.value})} />
                        </div>
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={newTherapist.email} onChange={(e) => setNewTherapist({...newTherapist, email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={newTherapist.phone} onChange={(e) => setNewTherapist({...newTherapist, phone: e.target.value})} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddTherapist(false)}>Cancel</Button>
                      <Button onClick={handleCreateTherapist}>Add Therapist</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {therapists.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <User className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No therapists added</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {therapists.map(therapist => (
                      <Card key={therapist.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarFallback>{therapist.first_name?.[0]}{therapist.last_name?.[0]}</AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-semibold">{therapist.full_name}</h4>
                              <p className="text-sm text-slate-500">{therapist.email}</p>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-sm text-slate-500">{therapist.phone || 'No phone'}</span>
                            {therapist.is_active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          {therapist.skills?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1">
                              {therapist.skills.map(skill => (
                                <Badge key={skill} variant="outline" className="text-xs">{skill}</Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Treatment Rooms</CardTitle>
                  <CardDescription>Manage spa treatment rooms</CardDescription>
                </div>
                <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-room-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Room
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Treatment Room</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Room Name</Label>
                        <Input value={newRoom.name} onChange={(e) => setNewRoom({...newRoom, name: e.target.value})} placeholder="e.g., Tranquility Suite" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Room Number</Label>
                          <Input value={newRoom.room_number} onChange={(e) => setNewRoom({...newRoom, room_number: e.target.value})} />
                        </div>
                        <div>
                          <Label>Capacity</Label>
                          <Input type="number" value={newRoom.capacity} onChange={(e) => setNewRoom({...newRoom, capacity: parseInt(e.target.value) || 1})} />
                        </div>
                      </div>
                      <div>
                        <Label>Room Type</Label>
                        <Select value={newRoom.room_type} onValueChange={(v) => setNewRoom({...newRoom, room_type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">Single</SelectItem>
                            <SelectItem value="couple">Couple</SelectItem>
                            <SelectItem value="group">Group</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddRoom(false)}>Cancel</Button>
                      <Button onClick={handleCreateRoom}>Add Room</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {rooms.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No treatment rooms configured</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {rooms.map(room => (
                      <Card key={room.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{room.name}</h4>
                            {room.room_number && <Badge variant="outline">{room.room_number}</Badge>}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="capitalize">{room.room_type}</span>
                            <span>Capacity: {room.capacity}</span>
                          </div>
                          {room.amenities?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {room.amenities.map(a => (
                                <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                              ))}
                            </div>
                          )}
                          {!room.is_active && <Badge variant="destructive" className="mt-2">Inactive</Badge>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
